import axios from "axios";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import {
  ArrowUpDown,
  Edit2,
  ListChecks,
  MoreHorizontalIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DataTable } from "../../components/DataTable";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { BaseUrlApi, ErrorMessage } from "../../lib/api";
import SkeletonTable from "../SkeletonTable";
import StarRating from "../../components/StarRating";
import { ShowAddSheet } from "./_components/ShowAddSheet";
import { ShowEditSheet } from "./_components/ShowEditSheet";
import { useNavigate } from "react-router-dom";
import { DialogDelete } from "../../components/Common/DialogDelete";

export default function Teams() {
  const navigate = useNavigate();

  // Teams
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teams, setTeams] = useState([]);
  const handleFetchTeams = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/teams`);
      setTeams(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingTeams(false);
    }
  };

  // Users
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const handleFetchUsers = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      setUsers(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    setLoadingUsers(true);
    handleFetchUsers();
  }, []);

  useEffect(() => {
    setLoadingTeams(true);
    handleFetchTeams();
  }, []);

  const [showEditSheet, setShowEditSheet] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const columns = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          اسم الفريق
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.name || "___",
    },
    {
      accessorKey: "description",
      header: "الوصف",
      cell: ({ row }) => row.original.description || "___",
    },
    {
      accessorKey: "created_at",
      header: "تاريخ الإنشاء",
      cell: ({ row }) =>
        row.original.created_at
          ? dayjs(row.original.created_at).format("DD/MM/YYYY hh:mm A")
          : "___",
    },
    {
      accessorKey: "updated_at",
      header: "تاريخ التحديث",
      cell: ({ row }) =>
        row.original.updated_at
          ? dayjs(row.original.updated_at).format("DD/MM/YYYY hh:mm A")
          : "___",
    },
    {
      accessorKey: "review",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          التقييم
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <StarRating rating={row.original.review || 0} />,
    },
    {
      accessorKey: "users",
      header: "عدد الأعضاء",
      cell: ({ row }) => {
        const users = row.original.users.length;
        return users !== 0 ? users : 0;
      },
    },

    {
      id: "Actions",
      header: "الإجراءات",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="justify-center">
            <DropdownMenuItem
              className="justify-center"
              onClick={() => navigate(`/team-tasks/${row.original.id}`)}
            >
              المهام <ListChecks />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center text-green-600"
              onClick={() => {
                setShowEditSheet(true);
                setEditRecord(row.original);
              }}
            >
              التعديل <Edit2 />
            </DropdownMenuItem>
            <DialogDelete
              title={"المهمة"}
              data={row.original.name}
              apiUrl={`/teams/${row.original.bid}`}
              fetchData={handleFetchTeams}
              className={"w-full"}
              isDrop={true}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const exportData = teams.map((row) => ({
    name: row.name || "___",
    description: row.description || "___",
    created_at: new Date(row.created_at).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
    updated_at: new Date(row.updated_at).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
    review: row.review || "___",
    users: row.users.length !== 0 ? row.users.length : 0,
  }));

  const exportColumns = [
    { accessorKey: "name", header: "اسم الفريق" },
    { accessorKey: "description", header: "الوصف" },
    { accessorKey: "created_at", header: "تاريخ الإنشاء" },
    { accessorKey: "updated_at", header: "تاريخ التعديل" },
    { accessorKey: "review", header: "التقييم" },
    { accessorKey: "users", header: "الأعضاء" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
      className="space-y-5"
    >
      <div className="flex items-center gap-2 justify-between">
        <h1 className="text-2xl font-bold">المجموعات</h1>
        <ShowAddSheet handleFetchTeams={handleFetchTeams} users={users} />
      </div>

      {loadingTeams || loadingUsers ? (
        <SkeletonTable />
      ) : (
        <DataTable
          columns={columns}
          data={teams}
          title={"المجموعات"}
          filterNames={["title", "description"]}
          exportData={exportData}
          exportColumns={exportColumns}
        />
      )}

      {showEditSheet && (
        <ShowEditSheet
          handleFetchTeams={handleFetchTeams}
          users={users}
          showEditSheet={showEditSheet}
          setShowEditSheet={setShowEditSheet}
          editRecord={editRecord}
          setEditRecord={setEditRecord}
        />
      )}
    </motion.div>
  );
}
